import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { forkJoin, fromEvent, Observable, of as observableOf, Subject } from 'rxjs';
import { finalize, catchError, tap, map, takeUntil, debounceTime, filter, distinctUntilChanged } from 'rxjs/operators';
import { MediaObserver } from '@angular/flex-layout';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
  DialogService,
  LanguageService,
  HelperService
} from 'src/app/services';
import { environment } from 'src/environments/environment';

import { ReportService } from '../report.service';
import { DetailedSettlementReportService } from './detailed-settlement-report.service';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipList } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CampaignsService } from '../../campaign-module/campaigns/campaigns.service';
import { requestDetailedSettlementReport } from 'src/app/model/requests';

interface DetailedSettlement {
  settlement_date: string;
  chain_name: string;
  store_name: string;
  terminal_id: string;
  transaction_date: string;
  coupon_number: string;
  campaign_description: string;
  amount: number;
  discount: number;
  handling_fee: number;
  item: string;
  quantity: number;
}

interface Filter {
  module_code: string;
  file_code: string | null;
  web_filter: WebFilter;
  filter?: {};
}

interface WebFilter {
  start_date: string;
  end_date: string;
  chain_id: number;
  island_group_id: number[];
  region_id: number[];
  province_id: number[];
  city_id: number[];
  campaign_id: number[];
  distribution_id: number[];
  category_id: number[];
  brand_id: number[];
  product_id: number[];
  limit: number;
  offset: number;
}

@Component({
  selector: 'esc-detailed-settlement-report',
  templateUrl: './detailed-settlement-report.component.html',
  styleUrls: ['./detailed-settlement-report.component.scss']
})

export class DetailedSettlementReportComponent implements OnInit {
  @ViewChild('chainAC') chainACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) chainACTr: MatAutocompleteTrigger;

  @ViewChild('chipsIslandGroup') chipsIslandGroup: MatChipList;
  @ViewChild('island_groupAC') islGrpACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) islGrpACTr: MatAutocompleteTrigger;

  @ViewChild('chipsRegion') chipsRegion: MatChipList;
  @ViewChild('regionAC') regionACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) regionACTr: MatAutocompleteTrigger;

  @ViewChild('chipsProvince') chipsProvince: MatChipList;
  @ViewChild('provinceAC') provinceACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) provinceACTr: MatAutocompleteTrigger;

  @ViewChild('cityProvince') cityProvince: MatChipList;
  @ViewChild('cityAC') cityACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) cityACTr: MatAutocompleteTrigger;

  @ViewChild('chipsCampaign') chipsCampaign: MatChipList;
  @ViewChild('campaignAC') campaignACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) campaignACTr: MatAutocompleteTrigger;

  @ViewChild('chipsCategory') chipsCategory: MatChipList;
  @ViewChild('categoryAC') categoryACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) categoryACTr: MatAutocompleteTrigger;

  @ViewChild('chipsBrand') chipsBrand: MatChipList;
  @ViewChild('brandAC') brandACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) brandACTr: MatAutocompleteTrigger;

  @ViewChild('chipsItem') chipsItem: MatChipList;
  @ViewChild('itemAC') itemACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) itemACTr: MatAutocompleteTrigger;

  constructor(
    private fb: FormBuilder,
    public media: MediaObserver,
    public campSvc: CampaignsService,
    private dialogService: DialogService,
    private service: DetailedSettlementReportService,
    private languageService: LanguageService,
    public helper: HelperService
  ) { }

  public defaultOption = {
    id: 0,
    code: "all",
    description: "All"
  };

  public loading = {
    export: false,
    chain: false,
    island_group: false,
    region: false,
    province: false,
    city: false,
    territory: false,
    campaign: false,
    distribution: false,
    category: false,
    brand: false,
    item: false,
    error: false
  };

  chains = [];
  island_groups = [];
  regions = [];
  provinces = [];
  cities = [];
  territories = [];
  campaigns = [];
  distributions = [];
  categories = [];
  brands = [];
  items = [];

  filterOptions = {
    chain: [],
    island_group: [],
    region: [],
    province: [],
    city: [],
    territory: [],
    campaign: [],
    distribution: [],
    category: [],
    brand: [],
    item: []
  }

  multiSelect = {
    selectedIslandGroups: [this.defaultOption],
    selectedRegions: [this.defaultOption],
    selectedProvinces: [this.defaultOption],
    selectedCities: [this.defaultOption],
    selectedTerritories: [this.defaultOption],
    selectedCampaigns: [this.defaultOption],
    selectedDistributions: [this.defaultOption],
    selectedCategories: [this.defaultOption],
    selectedBrands: [this.defaultOption],
    selectedItems: [this.defaultOption],
  };

  data;
  public maxStartDate = moment().toDate();
  public defaultStartDate = moment().toDate();
  public defaultEndDate = moment().toDate();
  public filters;
  public formGroup: FormGroup;
  public previousChainId: number = null;
  networkErrMsg = this.languageService.getTranslation("MESSAGE_ERROR_NETWORK_CONNECTION", {});
  
  private cancelRequest$ = new Subject<void>();

  ngOnInit() {
    this.initForm();
    this.detectFormChanges();
  }

  initForm() {
    this.formGroup = this.fb.group({
      settlement_start_date: [null, Validators.required],
      settlement_end_date: [null, Validators.required],
      chain: [{value: null, disabled: true}, Validators.required],
      island_group: [{value: this.defaultOption, disabled: true}, Validators.required],
      region: [{value: this.defaultOption, disabled: true}, Validators.required],
      province: [{value: this.defaultOption, disabled: true}, Validators.required],
      city: [{value: this.defaultOption, disabled: true}, Validators.required],
      territory: [{value: this.defaultOption, disabled: true}, Validators.required],
      campaign: [{value: this.defaultOption, disabled: true}, Validators.required],
      distribution: [{value: this.defaultOption, disabled: true}, Validators.required],
      category: [{value: this.defaultOption, disabled: true}, Validators.required],
      brand: [{value: this.defaultOption, disabled: true}, Validators.required],
      item: [{value: this.defaultOption, disabled: true}, Validators.required]
    })
  }

  detectFormChanges() {
    this.formGroup.get('settlement_start_date').valueChanges.subscribe(value => {
      if (value) {
        this.formGroup.get('settlement_end_date').reset();
        this.formGroup.get('settlement_end_date').markAsTouched();

        const maxEndDate = moment(value).add(30, 'days').toDate();
        const currentDate = moment().toDate();

        this.defaultEndDate = maxEndDate > currentDate ? currentDate : maxEndDate;
      }
      else {
        this.formGroup.get('settlement_end_date').reset();
        this.formGroup.get('settlement_end_date').markAsPristine();
      }

    });

    this.formGroup.get('settlement_end_date').valueChanges.subscribe(value => {
      this.formGroup.get('chain').setValue(null, {emitEvent: false})
      if (value) {
        this.formGroup.get('chain').enable({emitEvent: false})
      } else {
        this.formGroup.get('chain').disable({emitEvent: false})
      }
    });

    this.formGroup.get('chain').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.filterOptions['chain'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('chain')
    })

    this.formGroup.get('island_group').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.resetDependentFields('island_group');
      this.filterOptions['island_group'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('island_group')
    })

    this.formGroup.get('region').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.resetDependentFields('region');
      this.filterOptions['region'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('region')
    })

    this.formGroup.get('province').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.resetDependentFields('province');
      this.filterOptions['province'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('province')
    })

    this.formGroup.get('city').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.resetDependentFields('city');
      this.filterOptions['city'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('city')
    })

    this.formGroup.get('campaign').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.resetDependentFields('campaign');
      this.filterOptions['campaign'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('campaign')
    })

    this.formGroup.get('category').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.resetDependentFields('category');
      this.filterOptions['category'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('category')
    })

    this.formGroup.get('brand').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.resetDependentFields('brand');
      this.filterOptions['brand'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('brand')
    })

    this.formGroup.get('item').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.filterOptions['item'] = []
      this.filters.web_filter.offset = 0
      this.filters.web_filter.keyword = value
      this.getData('item')
    })
  }

  getInitialData(name) {
    this.autoCmpltClosed(name);
    this.filters = _.cloneDeep(INITIAL_OPTIONS);
    this.getData(name);
  }

  getMoreData(name) {
    if (this.filters.getMore) {
      this.filters.web_filter.offset += INITIAL_OPTIONS.web_filter.limit;
      this.getData(name);
    }
  }
  
  handleFilterResponse(res, filterName, filters, selectedArrayGroup=null) {
    // console.log(res,filterName,filters, selectedArrayGroup)
    this.loading[filterName] = false;
    if (environment.showLogs) {
      console.log(res);
    }
    if (res == null || res.status == 0 || res.status == 504 || res.status == 500) {
      this.loading.error = true;
      this.dialogService.openSnackBar(this.networkErrMsg)
      return;
    }
    if (_.has(res, 'value') && (res.value == "Success")) {
      if (!filters.web_filter.keyword && selectedArrayGroup) {
        this.filterOptions[filterName][0] = this.defaultOption;
      }

      this.filterOptions[filterName] = this.filterOptions[filterName].concat(res.data);
      if (selectedArrayGroup) {
        this.checkSelected(filterName, selectedArrayGroup, filterName);
      }

      if ((res.data.length > 0) && (this.filterOptions[filterName].length < _.get(res, 'count'))) {
        this.filters.getMore = true;
      }
    }
  }

  getData(name) {
    let filters = _.cloneDeep(this.filters)
    this.filters.getMore = false;
    this.loading[name] = true;

    switch (name) {
      case "chain": {
        this.filterOptions.chain = [];
        filters.web_filter["chain_type_ids"] = [0];
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = 0
        filters.web_filter.filters["island_group_id"] = [0]
        filters.web_filter.filters["region_id"] = [0]
        filters.web_filter.filters["province_id"] = [0]
        filters.web_filter.filters["city_id"] = [0]
        filters.web_filter.filters["campaign_id"] = [0]
        filters.web_filter.filters["category_id"] = [0]
        filters.web_filter.filters["brand_id"] = [0]
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getChainFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'chain',filters);
            this.loading.chain = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.chain = false;
          }
        });
        break;
      }
      case "island_group": {
        this.island_groups = [];
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = this.formGroup.get("chain").value == null ? 0 : this.formGroup.get("chain").value["id"]
        filters.web_filter.filters["island_group_id"] = [0]
        filters.web_filter.filters["region_id"] = [0]
        filters.web_filter.filters["province_id"] = [0]
        filters.web_filter.filters["city_id"] = [0]
        filters.web_filter.filters["campaign_id"] = [0]
        filters.web_filter.filters["category_id"] = [0]
        filters.web_filter.filters["brand_id"] = [0]
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getIslandGroupFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'island_group',filters,'selectedIslandGroups');
            this.loading.island_group = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.island_group = false;
          }
        });
        break;
      }
      case "region": {
        this.regions = [];
        // for(var island of this.formGroup.get("island_group").value){
        //   this.island_group_ids = this.island_group_ids.concat(island.id)
        // }
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = this.formGroup.get("chain").value['id'] || 0
        filters.web_filter.filters["island_group_id"] = _.map(this.multiSelect.selectedIslandGroups, "id")
        filters.web_filter.filters["region_id"] = [0]
        filters.web_filter.filters["province_id"] = [0]
        filters.web_filter.filters["city_id"] = [0]
        filters.web_filter.filters["campaign_id"] = [0]
        filters.web_filter.filters["category_id"] = [0]
        filters.web_filter.filters["brand_id"] = [0]
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getRegionFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'region',filters,'selectedRegions');
            this.loading.region = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.region = false;
          }
        });
        break;
      }
      case "province": {
        this.provinces = [];
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = this.formGroup.get("chain").value['id'] || 0
        filters.web_filter.filters["island_group_id"] = _.map(this.multiSelect.selectedIslandGroups, "id")
        filters.web_filter.filters["region_id"] = _.map(this.multiSelect.selectedRegions, "id")
        filters.web_filter.filters["province_id"] = [0]
        filters.web_filter.filters["city_id"] = [0]
        filters.web_filter.filters["campaign_id"] = [0]
        filters.web_filter.filters["category_id"] = [0]
        filters.web_filter.filters["brand_id"] = [0]
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getProvinceFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'province',filters,'selectedProvinces');
            this.loading.province = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.province = false;
          }
        });
        break;
      }

      case "city": {
        this.cities = [];
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = this.formGroup.get("chain").value['id'] || 0
        filters.web_filter.filters["island_group_id"] = _.map(this.multiSelect.selectedIslandGroups, "id")
        filters.web_filter.filters["region_id"] = _.map(this.multiSelect.selectedRegions, "id")
        filters.web_filter.filters["province_id"] = _.map(this.multiSelect.selectedProvinces, "id")
        filters.web_filter.filters["city_id"] = [0]
        filters.web_filter.filters["campaign_id"] = [0]
        filters.web_filter.filters["category_id"] = [0]
        filters.web_filter.filters["brand_id"] = [0]
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getCityFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'city',filters,'selectedCities');
            this.loading.city = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.city = false;
          }
        });
        break;
      }

      case "campaign": {
        this.campaigns = [];
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = this.formGroup.get("chain").value['id'] || 0
        filters.web_filter.filters["island_group_id"] = _.map(this.multiSelect.selectedIslandGroups, "id")
        filters.web_filter.filters["region_id"] = _.map(this.multiSelect.selectedRegions, "id")
        filters.web_filter.filters["province_id"] = _.map(this.multiSelect.selectedProvinces, "id")
        filters.web_filter.filters["city_id"] = _.map(this.multiSelect.selectedCities, "id")
        filters.web_filter.filters["campaign_id"] = [0]
        filters.web_filter.filters["category_id"] = [0]
        filters.web_filter.filters["brand_id"] = [0]
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getCampaignFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'campaign',filters,'selectedCampaigns');
            this.loading.campaign = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.campaign = false;
          }
        });
        break;
      }

      case "category": {
        this.categories = [];
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = this.formGroup.get("chain").value['id'] || 0
        filters.web_filter.filters["island_group_id"] = _.map(this.multiSelect.selectedIslandGroups, "id")
        filters.web_filter.filters["region_id"] = _.map(this.multiSelect.selectedRegions, "id")
        filters.web_filter.filters["province_id"] = _.map(this.multiSelect.selectedProvinces, "id")
        filters.web_filter.filters["city_id"] = _.map(this.multiSelect.selectedCities, "id")
        filters.web_filter.filters["campaign_id"] = _.map(this.multiSelect.selectedCampaigns, "id")
        filters.web_filter.filters["distribution_id"] = _.map(this.multiSelect.selectedDistributions, "id")
        filters.web_filter.filters["category_id"] = [0]
        filters.web_filter.filters["brand_id"] = [0]
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getCategoryFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'category',filters,'selectedCategories');
            this.loading.category = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.category = false;
          }
        });
        break;
      }

      case "brand": {
        this.brands = [];
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = this.formGroup.get("chain").value['id'] || 0
        filters.web_filter.filters["island_group_id"] = _.map(this.multiSelect.selectedIslandGroups, "id")
        filters.web_filter.filters["region_id"] = _.map(this.multiSelect.selectedRegions, "id")
        filters.web_filter.filters["province_id"] = _.map(this.multiSelect.selectedProvinces, "id")
        filters.web_filter.filters["city_id"] = _.map(this.multiSelect.selectedCities, "id")
        filters.web_filter.filters["campaign_id"] = _.map(this.multiSelect.selectedCampaigns, "id")
        filters.web_filter.filters["distribution_id"] = _.map(this.multiSelect.selectedDistributions, "id")
        filters.web_filter.filters["category_id"] = _.map(this.multiSelect.selectedCategories, "id")
        filters.web_filter.filters["brand_id"] = [0]
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getBrandFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'brand',filters,'selectedBrands');
            this.loading.brand = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.brand = false;
          }
        });
        break;
      }

      case "item": {
        this.items = [];
        filters.web_filter.filters["start_date"] = moment(this.formGroup.get("settlement_start_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["end_date"] = moment(this.formGroup.get("settlement_end_date").value).format("YYYY-MM-DD")
        filters.web_filter.filters["chain_id"] = this.formGroup.get("chain").value['id'] || 0
        filters.web_filter.filters["island_group_id"] = _.map(this.multiSelect.selectedIslandGroups, "id")
        filters.web_filter.filters["region_id"] = _.map(this.multiSelect.selectedRegions, "id")
        filters.web_filter.filters["province_id"] = _.map(this.multiSelect.selectedProvinces, "id")
        filters.web_filter.filters["city_id"] = _.map(this.multiSelect.selectedCities, "id")
        filters.web_filter.filters["campaign_id"] = _.map(this.multiSelect.selectedCampaigns, "id")
        filters.web_filter.filters["distribution_id"] = _.map(this.multiSelect.selectedDistributions, "id")
        filters.web_filter.filters["category_id"] = _.map(this.multiSelect.selectedCategories, "id")
        filters.web_filter.filters["brand_id"] = _.map(this.multiSelect.selectedBrands, "id")
        filters.web_filter.filters["product_id"] = [0]
        this.cancelRequest$.next();
        this.service.getSkuFilter(filters).pipe(
          takeUntil(this.cancelRequest$)
        ).subscribe({
          next: (res: any) => {
            this.handleFilterResponse(res,'item',filters,'selectedItems');
            this.loading.item = false;
          },
          error: (err) => {
            console.error(err);
            this.loading.item = false;
          }
        });
        break;
      }
    }
  }

  autoCmpltClosed(name) {
    this.filterOptions[name] = [];
  }

  resetControl(control: FormControl) {
    control.reset(null, { emitEvent: false });
  }

  displayFn(data?: any): string | undefined {
    return data ? _.get(data, 'description') : null;
  }

  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }

  checkAutocompleteObject(control, objectKey, targetArray) {
    if ((!_.isObject(control.controls[objectKey].value) || control.controls[objectKey].value.id == null) && (control.controls[objectKey].value != null && control.controls[objectKey].value != "")) {
      control.controls[objectKey].setErrors({
        "notExisting": true
      });
    }

    if (_.size(this.multiSelect[targetArray]) && targetArray) {
      control.controls[objectKey].setValue(null);
      this.assignFormValues(targetArray, objectKey);
    }
  }

  optionClicked(event: Event, data, srcArray, targetArray, formControlName, inputElem) {
    event.stopPropagation();
    this.toggleSelection(data, srcArray, targetArray, formControlName, inputElem);
  }

  resetDependentFields(formControlName) {
    const fieldHierarchy = {
      'island_group': ['region', 'province', 'city', 'campaign', 'category', 'brand', 'item'],
      'region': ['province', 'city', 'campaign', 'category', 'brand', 'item'],
      'province': ['city', 'campaign', 'category', 'brand', 'item'],
      'city': ['campaign', 'category', 'brand', 'item'],
      'campaign': ['category', 'brand', 'item'],
      'category': ['brand', 'item'],
      'brand': ['item']
    };

    const fieldsToReset = fieldHierarchy[formControlName];
    if (fieldsToReset) {
      fieldsToReset.forEach(field => {
        if (field === 'island_group') {
          this.multiSelect.selectedIslandGroups = [this.defaultOption];
        } else if (field === 'region') {
          this.multiSelect.selectedRegions = [this.defaultOption];
        } else if (field === 'province') {
          this.multiSelect.selectedProvinces = [this.defaultOption];
        } else if (field === 'city') {
          this.multiSelect.selectedCities = [this.defaultOption];
        } else if (field === 'campaign') {
          this.multiSelect.selectedCampaigns = [this.defaultOption];
        } else if (field === 'category') {
          this.multiSelect.selectedCategories = [this.defaultOption];
        } else if (field === 'brand') {
          this.multiSelect.selectedBrands = [this.defaultOption];
        } else if (field === 'item') {
          this.multiSelect.selectedItems = [this.defaultOption];
        }
        this.filterOptions[field].forEach(e => {
          e.selected = e.code === 'all';
        });
        this.formGroup.get(field).setValue([this.defaultOption], {emitEvent: false});
      });
    }
  }

  toggleSelection(data, srcArray, targetArray, formControlName, inputElem) {
    if (inputElem) {
      inputElem.value = '';
    }
    data.selected = !data.selected;

    //if 'all' is selected, choose only all
    if (data.selected == true && data.code == 'all') {
      srcArray.forEach(e => {
        e.selected = true;
      });
      this.multiSelect[targetArray] = [];
      this.multiSelect[targetArray].push(data);
      this.disableCheckbox(srcArray, formControlName, data);
    }

    //if 'all' is deselected, unselect everything
    else if (data.selected == false && data.code == 'all') {
      srcArray.forEach(e => {
        e.selected = false;
      });
      this.multiSelect[targetArray].splice(this.multiSelect[targetArray].indexOf(data), 1);
      this.enableCheckbox(srcArray);
    }

    else if (this.multiSelect[targetArray].indexOf(data) == -1) {
      //if other option is selected, check if 'all' is already selected and clear selection
      // this.multiSelect[targetArray].some(opt => opt.code == data.code)
      if (this.multiSelect[targetArray].indexOf(this.defaultOption) > -1) {
        srcArray.forEach(e => {
          e.selected = false;
        });
        this.multiSelect[targetArray] = [];
      }
      //if data exists in array, remove from list
      else if (this.multiSelect[targetArray].some(opt => opt.code == data.code)) {
        let index = _.findIndex(this.multiSelect[targetArray], function (item) { return item['code'] == data.code })
        this.multiSelect[targetArray].splice(index, 1);
      }
      else {
        this.multiSelect[targetArray].push(data);
      }
    }

    else {
      this.multiSelect[targetArray].splice(this.multiSelect[targetArray].indexOf(data), 1);
      srcArray.forEach(e => {
        if (e.id == data.id) {
          e.selected = false;
        }
      });
    }
    this.assignFormValues(targetArray, formControlName);
    this.resetDependentFields(formControlName);
  }

  enableCheckbox(optionArray) {
    optionArray.forEach(e => {
      e.disabled = false;
    })
  }

  disableCheckbox(optionArray, formControlName, data) {
    if (optionArray[0].selected && (_.has(this.formGroup.get(formControlName), 'value') && this.formGroup.get(formControlName).value == 0) || (_.get(data, 'code') == 'all')) {
      optionArray.forEach(e => {
        if (e.code != 'all') {
          e.disabled = true;
        }
      })
    } else {
      this.enableCheckbox(optionArray);
    }
  }

  assignFormValues(targetArray, formControlName) {
    let tempArr = this.multiSelect[targetArray].map(e => {
      let obj = {
        code: e.code,
        description: e.description
      }
      return obj;
    });

    let val = _.cloneDeep(tempArr)
    this.formGroup.controls[formControlName].patchValue(val);                                                                                   
  }

  checkSelected(srcArray, targetArray, formControlName) {
    if (this.multiSelect[targetArray].some(opt => opt.description == 'All')) {
      let tempObj = _.cloneDeep(this.defaultOption);
      tempObj['selected'] = false;
      this.toggleSelection(tempObj, this.filterOptions[srcArray], targetArray, formControlName, null);
    }
    else if (this.multiSelect[targetArray].length) {
      this.filterOptions[srcArray].forEach(e => {
        this.multiSelect[targetArray].forEach(v => {
          if (e.description == v.description) {
            e.selected = v.selected;
          }
        })
        if (e.description == 'All') {
          e.selected = false;
        }
      })
    } else {
      this[srcArray].forEach(e => {
        e.selected = false;
      })
    }
  }

  reset() {
    this.initForm()
    this.detectFormChanges()
    this.multiSelect.selectedIslandGroups = [this.defaultOption]
    this.multiSelect.selectedRegions = [this.defaultOption]
    this.multiSelect.selectedProvinces = [this.defaultOption]
    this.multiSelect.selectedCities = [this.defaultOption]
    this.multiSelect.selectedTerritories = [this.defaultOption]
    this.multiSelect.selectedCampaigns = [this.defaultOption]
    this.multiSelect.selectedDistributions = [this.defaultOption]
    this.multiSelect.selectedCategories = [this.defaultOption]
    this.multiSelect.selectedBrands = [this.defaultOption]
    this.multiSelect.selectedItems = [this.defaultOption]
  }

  autocompleteClose(name, input) {
    let controls = []
    let disableControls = true;
    switch(name) {
      case 'chain': {
        if ((!_.isObject(this.formGroup.get('chain').value) || this.formGroup.get('chain').value.id == null) && (this.formGroup.get('chain').value != null && this.formGroup.get('chain').value != "")) {
          input.value = ""
          this.formGroup.get('chain').setErrors({
            "notExisting": true
          });
        }
        controls = ['island_group', 'region', 'province', 'city', 'campaign', 'category', 'brand', 'item']
        if (_.isObject(this.formGroup.get('chain').value)) {
          disableControls = false
        }
        break;
      }
      case 'island_group': {
        controls = ['region', 'province', 'city', 'campaign', 'category', 'brand', 'item']
        if (this.multiSelect.selectedIslandGroups.length >= 1 && _.isObject(this.formGroup.get('chain').value)) {
          disableControls = false
        }
        break;
      }
      case 'region': {
        controls = ['province', 'city', 'campaign', 'category', 'brand', 'item']
        if (this.multiSelect.selectedRegions.length >= 1 && _.isObject(this.formGroup.get('chain').value)) {
          disableControls = false
        }
        break;
      }
      case 'province': {
        controls = ['city', 'campaign', 'category', 'brand', 'item']
        if (this.multiSelect.selectedProvinces.length >= 1 && _.isObject(this.formGroup.get('chain').value)) {
          disableControls = false
        }
        break;
      }
      case 'city': {
        controls = ['campaign', 'category', 'brand', 'item']
        if (this.multiSelect.selectedCities.length >= 1 && _.isObject(this.formGroup.get('chain').value)) {
          disableControls = false
        }
        break;
      }
      case 'campaign': {
        controls = ['category', 'brand', 'item']
        if (this.multiSelect.selectedCampaigns.length >= 1 && _.isObject(this.formGroup.get('chain').value)) {
          disableControls = false
        }
        break;
      }
      case 'category': {
        controls = ['brand', 'item']
        if (this.multiSelect.selectedCategories.length >= 1 && _.isObject(this.formGroup.get('chain').value)) {
          disableControls = false
        }
        break;
      }
      case 'brand': {
        controls = ['item']
        if (this.multiSelect.selectedBrands.length >= 1 && _.isObject(this.formGroup.get('chain').value)) {
          disableControls = false
        }
        break;
      }
      default: {

      }
    }

    _.forEach(controls, control => {
      if (disableControls) {
        this.formGroup.get(control).disable({emitEvent: false})
        this.formGroup.get(control).setValue([this.defaultOption], {emitEvent: false})

        if ('island_group' == control) {
          this.multiSelect.selectedIslandGroups = [this.defaultOption]
        } else if ('region' == control) {
          this.multiSelect.selectedRegions = [this.defaultOption]
        } else if ('province' == control) {
          this.multiSelect.selectedProvinces = [this.defaultOption]
        } else if ('city' == control) {
          this.multiSelect.selectedCities = [this.defaultOption]
        } else if ('campaign' == control) {
          this.multiSelect.selectedCampaigns = [this.defaultOption]
        } else if ('category' == control) {
          this.multiSelect.selectedCategories = [this.defaultOption]
        } else if ('brand' == control) {
          this.multiSelect.selectedBrands = [this.defaultOption]
        } else if ('item' == control) {
          this.multiSelect.selectedItems = [this.defaultOption]
        }
      } else {
        this.formGroup.get(control).enable({emitEvent: false})
      }
    })
  }

  optionSelected(input) {
    switch(input) {
      case 'chain': {
        const currentChainId = this.formGroup.get('chain').value?.id;
        if (this.previousChainId !== currentChainId) {
          this.multiSelect.selectedIslandGroups = [this.defaultOption]
          this.multiSelect.selectedRegions = [this.defaultOption]
          this.multiSelect.selectedProvinces = [this.defaultOption]
          this.multiSelect.selectedCities = [this.defaultOption]
          this.multiSelect.selectedCampaigns = [this.defaultOption]
          this.multiSelect.selectedCategories = [this.defaultOption]
          this.multiSelect.selectedBrands = [this.defaultOption]
          this.multiSelect.selectedItems = [this.defaultOption]
          this.previousChainId = currentChainId;
        }
        break;
      }
    }
  }

  isGenerateReportDisabled() {
    return !(
      this.formGroup.valid && 
      _.isObject(this.formGroup.get('chain').value) &&
      this.multiSelect.selectedIslandGroups.length > 0 &&
      this.multiSelect.selectedRegions.length > 0 &&
      this.multiSelect.selectedProvinces.length > 0 &&
      this.multiSelect.selectedCities.length > 0 &&
      this.multiSelect.selectedCampaigns.length > 0 &&
      this.multiSelect.selectedCategories.length > 0 &&
      this.multiSelect.selectedBrands.length > 0 &&
      this.multiSelect.selectedItems.length > 0
    )
  }

  generateReport() {
      this.loading.export = true;
  
      let filters = requestDetailedSettlementReport
      filters.web_filter.filters.start_date = moment(this.formGroup.get('settlement_start_date').value).format("YYYY-MM-DD")
      filters.web_filter.filters.end_date = moment(this.formGroup.get('settlement_end_date').value).format("YYYY-MM-DD")
      filters.web_filter.filters.chain_id = this.formGroup.get('chain').value.id
      filters.web_filter.filters.island_group_id = _.map(this.multiSelect.selectedIslandGroups, val => val.id)
      filters.web_filter.filters.region_id = _.map(this.multiSelect.selectedRegions, val => val.id)
      filters.web_filter.filters.province_id = _.map(this.multiSelect.selectedProvinces, val => val.id)
      filters.web_filter.filters.city_id = _.map(this.multiSelect.selectedCities, val => val.id)
      filters.web_filter.filters.campaign_id = _.map(this.multiSelect.selectedCampaigns, val => val.id)
      filters.web_filter.filters.category_id = _.map(this.multiSelect.selectedCategories, val => val.id)
      filters.web_filter.filters.brand_id = _.map(this.multiSelect.selectedBrands, val => val.id)
      filters.web_filter.filters.sku_id = _.map(this.multiSelect.selectedItems, val => val.id)
      filters.web_filter.full_name = localStorage.getItem("fullname")
  
      this.service
        .generate(filters)
        .pipe(
          finalize(() => {
            this.loading.export = false;
            return observableOf(null);
          })
        )
        .subscribe(
          (res: any) => {
            if (environment.showLogs) {
              console.log(res);
            }
  
            if (!res || res.status == 0 || res.status == 400) {
              this.dialogService.openSnackBar(this.networkErrMsg);
              return;
            } else {
              this.helper.reportGenerated();
            }
          },
          (err) => {
            if (environment.showLogs) {
              console.error(err);
            }
            this.dialogService.openSnackBar(this.networkErrMsg);
            return observableOf(null);
          }
        );
    }
}

const INITIAL_OPTIONS = {
  web_filter: {
    offset: 0,
    limit: 50,
    keyword: "",
    getMore: true,
    filter_type: "",
    filters: {
      start_date: "",
      end_date: "",
      chain_id: 0,
      island_group_id: [],
      region_id: [],
      province_id: [],
      city_id: [],
      campaign_id: [],
      distribution_id: [],
      category_id: [],
      brand_id: [],
      product_id: []
    }
  }
}